import ItemInformation from '@/components/general/ItemInforDetail';
import { ClockCustomIcon, LocationIcon, PetHospitalIcon, PetPrintIcon } from '@/components/icons';
import { APPOINTMENT_CANCELLED_BY, APPOINTMENT_STATUS, DATE_YMD_HMS_12H_FORMAT, DISPLAY_MOMENT_DATE_TIME_HM, DISPLAY_MOMENT_TIME_HM_AM_PM, DISPLAY_MOMENT_TIME_HMS, getShopCategoryName, ROUTES, SHOP_CATEGORY } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import { SHOP_CATEGORY_TYPE } from '@/types/enums';
import { ShopGeneralInfo } from '@/types/shop';
import { formatDateTime } from '@/utils/datetime';
import classNames from 'classnames';
import { CalendarDays, Phone, UserRound } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

export default observer(function GeneralInformation() {
    // hooks
    const { t } = useTranslation();
    // store
    const { appointmentStore: { appointment } } = useStore();

    return (
        <div className='p-3 bg-white rounded h-full'>
            <h3 className='font-semibold text-active text-[0.9375rem]'>{t('words_title.general_information')}</h3>
            <div className='mt-4 grid grid-cols-2 gap-x-2 gap-y-3 pb-2 border-b border-b-border-8'>
                <ItemInformation label={t('words_title.appointment_id')} value={appointment?.code} />
                <ItemInformation
                    label={t('words_title.appointment_time')}
                    value={(
                        <p className='flex gap-1'>
                            <CalendarDays />
                            {' '}
                            {formatDateTime(`${appointment?.appointmentDate} ${appointment?.appointmentTime}`, DISPLAY_MOMENT_DATE_TIME_HM)}
                        </p>
                    )}
                />
                <ItemInformation
                    label={t('words_title.appointment_created_date')}
                    value={formatDateTime(appointment?.createdAt, DATE_YMD_HMS_12H_FORMAT)}
                />
                <ItemInformation
                    label={t('words_title.last_updated')}
                    value={formatDateTime(appointment?.updatedAt, DATE_YMD_HMS_12H_FORMAT)}
                />
                <ItemInformation
                    label={t('words_title.status')}
                    value={(
                        <div className='flex w-fit px-2 py-1 items-center justify-start gap-1 bg-background-4 rounded-xl'>
                            <div className={classNames('w-2 h-2 rounded-full flex-none', appointment.status && APPOINTMENT_STATUS[appointment.status].color)}></div>
                            <div>{appointment?.status && t(`words_title.${APPOINTMENT_STATUS[appointment.status].key}`)}</div>
                        </div>
                    )}
                />
                {appointment?.cancelledBy && (
                    <ItemInformation
                        label={t('words_title.cancelled_by')}
                        value={
                            <p className=''>{t(APPOINTMENT_CANCELLED_BY[appointment.cancelledBy].key)}</p>
                        }
                    />
                )}
                <ItemInformation
                    label={t('words_title.referral_referral_code')}
                    value={
                        <p className=''>{appointment?.referralCode?.code ?? '--'}</p>
                    }
                />

            </div>
            <div className='mt-3'>
                <ShopInformation shopInfor={appointment?.shop} shopOwner={appointment?.shopOwner} />
            </div>
        </div>
    );
});

function ShopInformation({ shopInfor, shopOwner }: { shopInfor: ShopGeneralInfo, shopOwner: { fullName: string, code: string } }) {
    // hooks
    const { t } = useTranslation();
    const navigate = useNavigate();

    const getIconByCategoryIds = (cateIds) => {
        const view = {
            icon: <></>,
            text: ''
        };

        if (!(cateIds && cateIds.length > 0)) return view;

        if (cateIds.includes(SHOP_CATEGORY_TYPE.PetShop)) {
            view.icon = <PetPrintIcon className='h-4 w-4 fill-icon-4' />;
        } else if (cateIds.includes(SHOP_CATEGORY_TYPE.PetHospital)) {
            view.icon = <PetHospitalIcon className='h-5 w-5 fill-active' />;
        } else if (cateIds.includes(SHOP_CATEGORY_TYPE.Retail)) {
        }

        if (cateIds && cateIds.length > 0) {
            view.text = cateIds.filter(item => item !== SHOP_CATEGORY_TYPE.Retail).reduce((acc: string[], cur) => {
                acc.push(t(`select_options.${getShopCategoryName(cur)}`));
                return acc;
            }, []).join(' + ').replace(/\+ Pet/g, '+') ?? '';
        }

        return view;
    };

    return (
        <div className='grid grid-cols-[9rem,1fr] gap-3'>
            <div className='flex items-center min-w-32 min-h-32'>
                <img src={shopInfor?.images?.[0]?.image} className='h-32 w-32 rounded-[0.25rem]' alt='pet img' />
            </div>
            <div className='flex flex-col flex-1 gap-1 overflow-hidden'>
                <div className='min-h-7 bg-background-3 border-l border-l-icon-7 rounded-[0.125rem] flex items-center justify-start px-2 gap-1'>
                    {getIconByCategoryIds(shopInfor?.categoryIds).icon}
                    <p className='text-tab-1'>{getIconByCategoryIds(shopInfor?.categoryIds).text}</p>
                </div>
                <div
                    onClick={() => {
                        if (shopInfor?.isDeleted === false) {
                            navigate(`${ROUTES.shopManagement.href}/${shopInfor?.code}`);
                        }
                    }}
                    className={classNames('line-clamp-2 font-semibold leading-6', shopInfor?.isDeleted === false && 'text-link-1 cursor-pointer underline')}
                >
                    {shopInfor?.name}
                </div>
                <div className='flex gap-2 items-start'>
                    <div className='size-4 pt-[0.125rem]'>
                        <UserRound strokeWidth={0.25} className='size-4 fill-icon-2' />
                    </div>
                    <p className='text-[0.8125rem] text-tab-1 line-clamp-2 break-words flex-1'>{shopOwner?.fullName}</p>
                </div>
                <div className='flex gap-2 items-center'>
                    <div className='size-4'>
                        <ClockCustomIcon className='size-4 fill-icon-2' />
                    </div>
                    <p className='text-tab-1 text-[0.8125rem] break-words'>
                        {formatDateTime(shopInfor?.generalOpenTime, DISPLAY_MOMENT_TIME_HM_AM_PM, {
                            onlyTime: true,
                            currentformat: DISPLAY_MOMENT_TIME_HMS
                        })}
                        {' '}
                        -
                        {' '}
                        {formatDateTime(shopInfor?.generalCloseTime, DISPLAY_MOMENT_TIME_HM_AM_PM, {
                            onlyTime: true,
                            currentformat: DISPLAY_MOMENT_TIME_HMS
                        })}
                    </p>
                </div>
                <div className='flex gap-2 items-center'>
                    <Phone strokeWidth={0.25} className='size-4 fill-icon-2' />
                    <p className='text-[0.8125rem] text-tab-1'>{shopInfor?.phoneNumber}</p>
                </div>

                <div className='flex gap-2'>
                    <div className='pt-1'><LocationIcon className='size-4 fill-icon-2' /></div>
                    <p className='text-[0.8125rem] text-tab-1 line-clamp-2'>{shopInfor?.address}</p>
                </div>
            </div>
        </div>
    );
};
